import * as THREE from 'three';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { Hud } from '../systems/Hud';
import { FeedbackFlash } from '../ui/FeedbackFlash';
import { IntroPanel } from '../ui/IntroPanel';
import { TeacherStatsPanel } from '../ui/TeacherStatsPanel';
import { Village } from '../scene/Village';
import { SortingStation } from '../scene/SortingStation';
import { tierForScore, tierIndexForScore } from '../scene/tiers';
import { ScoreController } from './ScoreController';
import { RoundState } from './RoundState';
import { TeacherStats } from './TeacherStats';
import { ScanController } from '../qr/ScanController';
import { ScanView } from '../ui/ScanView';
import { AnswerCard } from '../ui/AnswerCard';
import { SummaryPanel } from '../ui/SummaryPanel';
import { CARDS, type WasteCard } from '../data/cards';
import type { BinId } from '../data/bins';

const TRANSFORM_CAPTION_MS = 1600;
// Matches SortingStation's item-fly animation — wait for the item to actually arrive at the bin
// before popping up the "what it became" reveal in the now-empty hover spot.
const TRANSFORM_REVEAL_DELAY_MS = 400;

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
  private readonly cameraRig: CameraRig;
  private readonly village: Village;
  private readonly sortingStation: SortingStation;
  private readonly hud = new Hud();
  private readonly audio = new AudioSystem();
  private readonly feedbackFlash = new FeedbackFlash();
  private readonly scoreController = new ScoreController();
  private readonly round = new RoundState();
  private readonly teacherStats = new TeacherStats();
  private readonly scan: ScanController;
  private readonly scanView: ScanView;
  private readonly answerCard: AnswerCard;
  private readonly summary: SummaryPanel;
  private readonly teacherStatsPanel: TeacherStatsPanel;
  private readonly debugTools: DebugTools;
  private readonly loop = new Loop(
    (delta) => this.update(delta),
    () => this.render(),
  );

  private readonly tuning: DebugTuning = { exposure: 1.05, maxDpr: 2, debugScore: 50 };
  private frame = 0;
  // 수업용 축소 모드(?cards=10): 앞에서부터 N장만 라운드에 사용한다. 인쇄 카드 번호와 그대로 대응.
  private readonly deck: WasteCard[];
  private activeCards: WasteCard[];
  private pendingCard: WasteCard | null = null;
  private pendingAttempt: 1 | 2 = 1;
  private awaitingChoice = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const params = new URLSearchParams(window.location.search);
    const deckSize = Number(params.get('cards'));
    this.deck =
      Number.isInteger(deckSize) && deckSize >= 5 && deckSize < CARDS.length
        ? CARDS.slice(0, deckSize)
        : CARDS;
    this.activeCards = this.deck;

    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;

    this.village = new Village(this.scene);
    this.cameraRig = new CameraRig(this.camera);
    this.sortingStation = new SortingStation(this.scene, this.camera, this.getAppElement(), CARDS);

    this.debugTools = new DebugTools(
      this.tuning,
      () => {
        this.renderer.toneMappingExposure = this.tuning.exposure;
        resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
      },
      (value) => this.village.setScore(value),
    );

    this.scan = new ScanController(this.getVideoElement());
    this.scanView = new ScanView(
      CARDS,
      (card) => this.presentCard(card),
      (card) => this.activeCards.includes(card) && !this.round.hasSeen(card.id),
    );
    this.answerCard = new AnswerCard(
      (bin) => this.handleChoice(bin),
      () => this.afterReveal(),
      () => this.retryChoice(),
    );
    this.summary = new SummaryPanel(
      () => this.restart(),
      () => this.startRetryRound(),
    );
    new IntroPanel(() => void this.handleIntroStart());
    this.teacherStatsPanel = new TeacherStatsPanel(this.teacherStats, CARDS);

    this.scan.onScan((result) => {
      if (result.status === 'error') {
        console.warn(result.message);
        this.scanView.showCameraError();
        return;
      }
      const card = CARDS.find((candidate) => candidate.qrValue === result.qrValue);
      if (card) this.presentCard(card);
    });

    this.setupMuteToggle();
    this.setupSortingInput();

    // Testing hook — lets Playwright drive the reactive scene without a real camera.
    window.__debugApplyScore = (score: number) => this.village.setScore(score);

    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    this.sortingStation.resize(this.canvas.clientWidth, this.canvas.clientHeight);
    this.sortingStation.setVisible(false);
    this.village.setScore(this.scoreController.getState().cleanliness);
    this.refreshHud();

    if (params.has('teacher')) {
      this.teacherStatsPanel.show();
    }
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.scan.dispose();
    this.audio.dispose();
    this.debugTools.dispose();
    this.village.dispose();
    this.sortingStation.dispose();
    this.renderer.dispose();
    window.__debugApplyScore = undefined;
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
  }

  private async handleIntroStart(): Promise<void> {
    await this.audio.unlock();
    this.audio.startAmbient();
    this.audio.setTier(tierIndexForScore(this.scoreController.getState().cleanliness));
    this.scanView.show();
    void this.scan.start();
  }

  private setupSortingInput(): void {
    this.canvas.addEventListener('pointerdown', (event) => {
      if (!this.awaitingChoice) return;
      const rect = this.canvas.getBoundingClientRect();
      const ndcX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      const bin = this.sortingStation.pickBinAt(ndcX, ndcY);
      if (bin) this.handleChoice(bin);
    });
  }

  private getAppElement(): HTMLElement {
    const app = document.querySelector<HTMLElement>('#app');
    if (!app) throw new Error('Missing #app element.');
    return app;
  }

  private showTransformCaption(text: string): void {
    const el = document.querySelector<HTMLElement>('#transform-caption');
    if (!el) return;
    el.textContent = `♻️ ${text}`;
    el.hidden = false;
  }

  private hideTransformCaption(): void {
    const el = document.querySelector<HTMLElement>('#transform-caption');
    if (el) el.hidden = true;
  }

  private setupMuteToggle(): void {
    const button = document.querySelector<HTMLButtonElement>('#mute-toggle');
    if (!button) return;
    button.addEventListener('click', () => {
      const muted = !this.audio.isMuted();
      this.audio.setMuted(muted);
      button.textContent = muted ? '🔇' : '🔊';
      button.setAttribute('aria-label', muted ? '소리 켜기' : '소리 끄기');
    });
  }

  private presentCard(card: WasteCard): void {
    if (!this.activeCards.includes(card)) return; // not part of the current (possibly retry) round
    if (this.round.hasSeen(card.id)) return; // avoid double-counting a re-scan mid-session
    this.pendingCard = card;
    this.pendingAttempt = 1;
    this.scanView.hide();
    this.cameraRig.snapToSortView();
    this.cameraRig.freeze();
    this.sortingStation.setVisible(true);
    this.sortingStation.showItem(card);
    this.awaitingChoice = true;
    this.answerCard.showChoose(card);
  }

  private retryChoice(): void {
    if (!this.pendingCard) return;
    this.answerCard.showChoose(this.pendingCard);
  }

  private handleChoice(bin: BinId): void {
    if (!this.pendingCard) return;
    const card = this.pendingCard;
    const correct = bin === card.correctBin;

    if (this.pendingAttempt === 1 && !correct) {
      this.teacherStats.recordMiss(card.id);
      this.pendingAttempt = 2;
      this.answerCard.showHint(card);
      return;
    }

    this.awaitingChoice = false;
    this.sortingStation.flyItemTo(bin);

    const hintUsed = this.pendingAttempt === 2;
    this.round.record({ card, chosenBin: bin, correct, hintUsed });
    const state = this.scoreController.applyAnswer(correct, card.pollutionDelta);
    this.village.setScore(state.cleanliness);
    this.village.triggerAnswerBurst(correct);
    this.feedbackFlash.trigger(correct);
    this.audio.setTier(tierIndexForScore(state.cleanliness));
    if (correct) this.audio.correct();
    else this.audio.incorrect();

    // Skip the "reborn as something new" spectacle for real general-waste items — nothing is
    // actually reborn when the correct answer is "this just goes to landfill/incineration."
    if (correct && card.correctBin !== 'general') {
      this.showTransformCaption(card.explanation);
      window.setTimeout(() => {
        this.sortingStation.triggerTransformSpark(bin);
        this.sortingStation.showTransformResult(card.becomesIconRef);
      }, TRANSFORM_REVEAL_DELAY_MS);
      window.setTimeout(() => {
        this.hideTransformCaption();
        this.answerCard.showReveal(card, correct, state.lastComboBonus, hintUsed);
        this.refreshHud();
      }, TRANSFORM_CAPTION_MS);
    } else {
      this.answerCard.showReveal(card, correct, state.lastComboBonus, hintUsed);
      this.refreshHud();
    }
  }

  private afterReveal(): void {
    this.pendingCard = null;
    this.answerCard.hide();
    this.cameraRig.resume();
    this.sortingStation.hideItem();
    this.sortingStation.setVisible(false);

    if (this.round.getEntries().length >= this.activeCards.length) {
      this.showSummary();
      return;
    }

    this.scanView.show();
  }

  private showSummary(): void {
    const state = this.scoreController.getState();
    const tier = tierForScore(state.cleanliness);
    this.summary.show(
      state.correctCount,
      this.activeCards.length,
      tier.labelKo,
      tier.outroKo,
      this.round.getSubTopicStats(),
      this.round.getWrongCards().length,
    );
  }

  private startRetryRound(): void {
    const wrongCards = this.round.getWrongCards();
    if (wrongCards.length === 0) return;
    this.activeCards = wrongCards;
    this.round.reset();
    this.summary.hide();
    this.refreshHud();
    this.scanView.show();
  }

  private restart(): void {
    this.activeCards = this.deck;
    this.round.reset();
    this.scoreController.reset();
    this.village.setScore(this.scoreController.getState().cleanliness);
    this.audio.setTier(tierIndexForScore(this.scoreController.getState().cleanliness));
    this.summary.hide();
    this.refreshHud();
    this.scanView.show();
  }

  private refreshHud(): void {
    const state = this.scoreController.getState();
    this.hud.update(
      state.cleanliness,
      this.round.getEntries().length,
      this.activeCards.length,
      this.village.currentTierLabel(),
      state.streak,
    );
  }

  private update(delta: number): void {
    this.frame += 1;
    if (resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr)) {
      this.sortingStation.resize(this.canvas.clientWidth, this.canvas.clientHeight);
    }
    this.cameraRig.update(delta);
    this.village.update(delta);
    this.sortingStation.update(delta);
    this.publishDiagnostics();
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
    this.sortingStation.renderLabels();
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    const state = this.scoreController.getState();
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      score: state.cleanliness,
      tier: this.village.currentTierLabel(),
      answeredCount: this.round.getEntries().length,
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
      },
    };
  }

  private getVideoElement(): HTMLVideoElement {
    const video = document.querySelector<HTMLVideoElement>('#scan-video');
    if (!video) throw new Error('Missing #scan-video element.');
    return video;
  }
}
